import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy, TrendingUp, TrendingDown, Shield, Activity, Target, BarChart3, Loader2,
  Crown, Medal, Users, DollarSign, Lock, Eye, EyeOff, ExternalLink, Sparkles, Award,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { InfoTip } from '../components/ui';
import {
  getTraderLeaderboard, getLeaderboardCategories,
  getLeaderboardMyStatus, setLeaderboardOptIn,
} from '../services/api';

const CATEGORY_ICON = {
  return: TrendingUp,
  drawdown: Shield,
  sharpe: BarChart3,
  winrate: Target,
  volume: Activity,
};

const TIER_COLOR = {
  trial:   'text-gray-400 bg-gray-500/10 border-gray-500/20',
  starter: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  trader:  'text-violet-400 bg-violet-500/10 border-violet-500/20',
  pro:     'text-amber-400 bg-amber-500/10 border-amber-500/20',
  elite:   'text-pink-400 bg-pink-500/10 border-pink-500/20',
};

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function formatCompactUsd(v) {
  if (v == null || !Number.isFinite(Number(v))) return '$—';
  const n = Math.abs(Number(v));
  const sign = Number(v) < 0 ? '-' : '';
  if (n >= 1_000_000) return `${sign}$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${sign}$${(n / 1_000).toFixed(1)}K`;
  return `${sign}$${n.toFixed(0)}`;
}

export default function TopTradersPage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('return');
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myStatus, setMyStatus] = useState(null);
  const [optInBusy, setOptInBusy] = useState(false);
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    Promise.all([
      getLeaderboardCategories().then(r => r?.data || []).catch(() => []),
      getLeaderboardMyStatus().then(r => r?.data).catch(() => null),
    ]).then(([cats, status]) => {
      setCategories(cats);
      setMyStatus(status);
      if (status?.displayName) setDisplayName(status.displayName);
    });
  }, []);

  async function loadBoard(cat) {
    setLoading(true);
    try {
      const res = await getTraderLeaderboard(cat, 50);
      setBoard(res?.data || res);
    } catch (err) {
      console.error('Leaderboard load failed', err);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadBoard(activeCategory); }, [activeCategory]);

  async function refreshMyStatus() {
    try {
      const res = await getLeaderboardMyStatus();
      setMyStatus(res?.data);
    } catch {}
  }

  async function handleToggleOptIn(enabled) {
    setOptInBusy(true);
    try {
      await setLeaderboardOptIn(enabled, enabled ? (displayName || null) : null);
      await refreshMyStatus();
      await loadBoard(activeCategory);
    } catch (err) {
      alert('Update failed: ' + err.message);
    } finally {
      setOptInBusy(false);
    }
  }

  const entries = board?.entries || [];
  const podium = entries.slice(0, 3);
  const restOfList = entries.slice(3);
  const agg = board?.aggregateStats;

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/10 via-dark-800/40 to-dark-800/40 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-emerald-400/5 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Trophy className="w-5 h-5 text-dark-900" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Top Traders</h1>
              <p className="text-xs text-amber-300/80 mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Real CCXT trade history · 30-day window · Opt-in only
                <InfoTip side="bottom">5 categories. Filter by Sharpe + Drawdown, not raw Return. Top 3 podium + top 50 list. Aggregate stats show cohort medians for context.</InfoTip>
              </p>
            </div>
          </div>

          {/* Aggregate hero stats */}
          {agg && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
              <HeroStat
                icon={Users}
                label="Qualifying traders"
                value={agg.totalCandidates}
                accent="text-amber-400"
              />
              <HeroStat
                icon={TrendingUp}
                label="In profit (30d)"
                value={`${agg.profitableCount}/${agg.totalCandidates || 0}`}
                accent="text-emerald-400"
              />
              <HeroStat
                icon={BarChart3}
                label="Avg return"
                value={`${agg.avgReturnPct >= 0 ? '+' : ''}${formatNum(agg.avgReturnPct, 2)}%`}
                accent={agg.avgReturnPct >= 0 ? 'text-emerald-400' : 'text-red-400'}
              />
              <HeroStat
                icon={DollarSign}
                label="Total volume"
                value={formatCompactUsd(agg.totalVolume)}
                accent="text-violet-400"
              />
            </div>
          )}
        </div>
      </div>

      {/* ─── Opt-in card ──────────────────────────────────────────────────── */}
      <OptInCard
        myStatus={myStatus}
        optInBusy={optInBusy}
        displayName={displayName}
        setDisplayName={setDisplayName}
        onToggle={handleToggleOptIn}
      />

      {/* ─── Category tabs ────────────────────────────────────────────────── */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {categories.map(c => {
          const Icon = CATEGORY_ICON[c.id] || BarChart3;
          const active = activeCategory === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActiveCategory(c.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border whitespace-nowrap text-sm transition-all flex-shrink-0 ${
                active
                  ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/10 border-amber-400/40 text-amber-300 shadow-lg shadow-amber-500/10'
                  : 'bg-dark-800/50 border-dark-700/50 text-gray-400 hover:border-dark-600 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" /> {c.label}
            </button>
          );
        })}
      </div>

      {/* ─── Leaderboard body ─────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-amber-400" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          {/* Podium (top 3) */}
          {podium.length > 0 && <Podium entries={podium} />}

          {/* The rest */}
          {restOfList.length > 0 && (
            <RankedList entries={restOfList} startRank={podium.length + 1} category={board.categoryMeta} />
          )}
        </div>
      )}

      <p className="text-[10px] text-gray-600 text-center">
        {board?.totalCandidates ?? 0} eligible traders · 30-day window · cached 5 min · last refreshed{' '}
        {board?.computedAt ? new Date(board.computedAt).toLocaleTimeString() : '—'}
      </p>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function HeroStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-dark-900/40 backdrop-blur-sm rounded-xl border border-dark-700/40 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent || 'text-gray-400'}`} />
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      </div>
      <p className={`text-xl font-bold font-mono ${accent || 'text-white'}`}>{value}</p>
    </div>
  );
}

function OptInCard({ myStatus, optInBusy, displayName, setDisplayName, onToggle }) {
  if (!myStatus) return null;
  const optedIn = !!myStatus.optedIn;
  const eligible = !!myStatus.eligibleForLeaderboard;

  return (
    <div
      className={`rounded-2xl border p-5 transition-all ${
        optedIn
          ? 'bg-emerald-500/5 border-emerald-500/30'
          : 'bg-dark-800/50 border-dark-700/50'
      }`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-[280px]">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              optedIn
                ? 'bg-emerald-500/15 text-emerald-400'
                : 'bg-dark-700 text-gray-500'
            }`}
          >
            {optedIn ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">
              {optedIn ? 'You are on the leaderboard' : 'You are NOT on the leaderboard'}
            </p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              {optedIn
                ? 'Your stats are visible to everyone in the categories you qualify for. Toggle off any time.'
                : eligible
                ? 'Your track record qualifies. Opt in to compete with other traders.'
                : 'Need ≥5 verified round-trips in the last 30 days. Visit Verify Track Record to refresh.'}
            </p>
            {optedIn && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value.slice(0, 32))}
                  placeholder="Display name (optional)"
                  maxLength={32}
                  className="bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-xs w-52"
                />
                <button
                  onClick={() => onToggle(true)}
                  disabled={optInBusy}
                  className="text-xs px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20"
                >
                  Save name
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          {!eligible && !optedIn ? (
            <Link
              to="/seller-verify"
              className="text-xs px-4 py-2 rounded-lg border border-dark-600 text-gray-300 hover:border-dark-500 flex items-center gap-1.5"
            >
              <Lock className="w-3 h-3" /> Verify track record first
            </Link>
          ) : (
            <button
              onClick={() => onToggle(!optedIn)}
              disabled={optInBusy}
              className={`text-xs px-5 py-2.5 rounded-lg font-semibold transition-all ${
                optedIn
                  ? 'bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20'
                  : 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-dark-900 hover:from-emerald-400 hover:to-emerald-300 shadow-lg shadow-emerald-500/20'
              } disabled:opacity-50`}
            >
              {optInBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : optedIn ? 'Opt out' : 'Opt in'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
      <div className="w-16 h-16 rounded-2xl bg-dark-700 mx-auto mb-4 flex items-center justify-center">
        <Trophy className="w-8 h-8 text-gray-600" />
      </div>
      <p className="text-white font-medium mb-1">No traders qualify yet</p>
      <p className="text-sm text-gray-500 mb-4">
        Need ≥5 round-trips in the last 30 days + opt-in. Be the first.
      </p>
      <Link
        to="/seller-verify"
        className="inline-flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 px-3 py-1.5 rounded-lg border border-amber-400/30 bg-amber-500/5"
      >
        Refresh your track record <ExternalLink className="w-3 h-3" />
      </Link>
    </div>
  );
}

function Podium({ entries }) {
  // Render order: 2nd, 1st, 3rd (visual centering with 1st elevated)
  const podiumOrder = [];
  if (entries[1]) podiumOrder.push({ entry: entries[1], rank: 2 });
  if (entries[0]) podiumOrder.push({ entry: entries[0], rank: 1 });
  if (entries[2]) podiumOrder.push({ entry: entries[2], rank: 3 });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {podiumOrder.map(({ entry, rank }) => (
        <PodiumCard key={entry.userId} entry={entry} rank={rank} />
      ))}
    </div>
  );
}

function PodiumCard({ entry, rank }) {
  const config = {
    1: {
      Icon: Crown,
      iconColor: 'text-amber-300',
      ringColor: 'ring-amber-400/40',
      borderColor: 'border-amber-400/30',
      bgGrad: 'from-amber-500/15 via-amber-500/5 to-transparent',
      shadow: 'shadow-amber-500/20',
      label: '1st',
      labelColor: 'text-amber-300',
      elevation: 'sm:-translate-y-3',
    },
    2: {
      Icon: Medal,
      iconColor: 'text-gray-200',
      ringColor: 'ring-gray-400/30',
      borderColor: 'border-gray-500/30',
      bgGrad: 'from-gray-400/10 via-gray-500/5 to-transparent',
      shadow: 'shadow-gray-500/10',
      label: '2nd',
      labelColor: 'text-gray-300',
      elevation: '',
    },
    3: {
      Icon: Award,
      iconColor: 'text-orange-400',
      ringColor: 'ring-orange-600/30',
      borderColor: 'border-orange-600/30',
      bgGrad: 'from-orange-600/10 via-orange-600/5 to-transparent',
      shadow: 'shadow-orange-700/10',
      label: '3rd',
      labelColor: 'text-orange-400',
      elevation: '',
    },
  }[rank];

  const initial = (entry.displayName || '?').slice(0, 1).toUpperCase();
  const isProfit = (entry.netPnl ?? 0) >= 0;
  const tierStyle = TIER_COLOR[entry.tier] || TIER_COLOR.trial;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border ${config.borderColor} bg-gradient-to-b ${config.bgGrad} backdrop-blur-sm p-5 transition-transform ${config.elevation} hover:-translate-y-1 shadow-xl ${config.shadow}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-bold uppercase tracking-wider ${config.labelColor}`}>{config.label}</span>
        <config.Icon className={`w-5 h-5 ${config.iconColor}`} />
      </div>

      <div className="flex items-center gap-3 mb-4">
        {entry.avatarUrl ? (
          <img
            src={entry.avatarUrl}
            alt=""
            className={`w-12 h-12 rounded-full object-cover ring-2 ${config.ringColor}`}
          />
        ) : (
          <div className={`w-12 h-12 rounded-full bg-dark-700 flex items-center justify-center text-base font-bold text-white ring-2 ${config.ringColor}`}>
            {initial}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate">{entry.displayName}</p>
          <p className="text-[10px] text-gray-500 capitalize flex items-center gap-1">
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${tierStyle}`}>{entry.tier}</span>
            <span>{entry.roundTrips} trips</span>
          </p>
        </div>
      </div>

      {/* Sparkline */}
      {Array.isArray(entry.sparkline) && entry.sparkline.length >= 2 && (
        <div className="mb-3 h-10">
          <Sparkline values={entry.sparkline} isProfit={isProfit} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-dark-900/40 rounded-lg p-2">
          <p className="text-[9px] uppercase tracking-wider text-gray-500">Net P&L</p>
          <p className={`text-base font-bold font-mono ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
            {isProfit ? '+' : ''}{formatCompactUsd(entry.netPnl)}
          </p>
        </div>
        <div className="bg-dark-900/40 rounded-lg p-2">
          <p className="text-[9px] uppercase tracking-wider text-gray-500">Return</p>
          <p className={`text-base font-bold font-mono ${entry.netPnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {entry.netPnlPct >= 0 ? '+' : ''}{formatNum(entry.netPnlPct, 1)}%
          </p>
        </div>
        <div className="bg-dark-900/40 rounded-lg p-2">
          <p className="text-[9px] uppercase tracking-wider text-gray-500">Win rate</p>
          <p className="text-base font-bold font-mono text-white">{formatNum(entry.winRate, 0)}%</p>
        </div>
        <div className="bg-dark-900/40 rounded-lg p-2">
          <p className="text-[9px] uppercase tracking-wider text-gray-500">Max DD</p>
          <p className="text-base font-bold font-mono text-red-400">-{formatNum(entry.maxDrawdownPct, 1)}%</p>
        </div>
      </div>
    </div>
  );
}

function RankedList({ entries, startRank, category }) {
  return (
    <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 overflow-hidden">
      {category && (
        <div className="px-5 py-3 border-b border-dark-700/50 bg-dark-800/60">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
            {category.label} · ranked {startRank} and below
          </p>
        </div>
      )}
      <div className="divide-y divide-dark-700/40">
        {entries.map((e, i) => (
          <RankedRow key={e.userId} entry={e} rank={startRank + i} />
        ))}
      </div>
    </div>
  );
}

function RankedRow({ entry, rank }) {
  const isProfit = (entry.netPnl ?? 0) >= 0;
  const tierStyle = TIER_COLOR[entry.tier] || TIER_COLOR.trial;
  const initial = (entry.displayName || '?').slice(0, 1).toUpperCase();
  return (
    <div className="flex items-center gap-3 p-4 hover:bg-dark-700/20 transition-colors">
      <div className="w-8 h-8 rounded-lg bg-dark-700 text-gray-400 flex items-center justify-center text-xs font-bold flex-shrink-0">
        {rank}
      </div>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {entry.avatarUrl ? (
          <img src={entry.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-dark-700 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
            {initial}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-bold text-white truncate">{entry.displayName}</p>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold capitalize ${tierStyle}`}>{entry.tier}</span>
          </div>
          <p className="text-[10px] text-gray-500">{entry.roundTrips} trips · {entry.windowDays}d window</p>
        </div>
      </div>

      {/* Inline sparkline (hidden on small) */}
      {Array.isArray(entry.sparkline) && entry.sparkline.length >= 2 && (
        <div className="hidden md:block w-24 h-8 flex-shrink-0">
          <Sparkline values={entry.sparkline} isProfit={isProfit} />
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-right flex-shrink-0">
        <Metric label="Net P&L" value={`${isProfit ? '+' : ''}${formatCompactUsd(entry.netPnl)}`} accent={isProfit ? 'emerald' : 'red'} />
        <Metric label="Return" value={`${entry.netPnlPct >= 0 ? '+' : ''}${formatNum(entry.netPnlPct, 2)}%`} accent={entry.netPnlPct >= 0 ? 'emerald' : 'red'} />
        <Metric label="Win rate" value={`${formatNum(entry.winRate, 1)}%`} hideOnMobile />
        <Metric label="Max DD" value={`-${formatNum(entry.maxDrawdownPct, 1)}%`} accent="red" hideOnMobile />
      </div>
    </div>
  );
}

function Metric({ label, value, accent, hideOnMobile }) {
  const colorMap = { emerald: 'text-emerald-400', red: 'text-red-400' };
  return (
    <div className={hideOnMobile ? 'hidden sm:block' : ''}>
      <p className="text-[9px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-xs font-bold font-mono ${colorMap[accent] || 'text-white'}`}>{value}</p>
    </div>
  );
}

function Sparkline({ values, isProfit }) {
  if (!Array.isArray(values) || values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 100, h = 30;
  const step = w / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`)
    .join(' ');
  const color = isProfit ? '#34d399' : '#f87171';
  const fillColor = isProfit ? 'rgba(52,211,153,0.10)' : 'rgba(248,113,113,0.10)';
  // Build area fill: include corners at baseline
  const areaPoints = `0,${h} ${points} ${w},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%" preserveAspectRatio="none">
      <polygon points={areaPoints} fill={fillColor} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
